/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskCategoriesRequest,
  ListRiskCategoriesSecurity,
} from "../models/operations/listriskcategories.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskCategoriesQuery,
  prefetchRiskCategories,
  queryKeyRiskCategories,
  RiskCategoriesQueryData,
} from "./riskCategories.core.js";
export {
  buildRiskCategoriesQuery,
  prefetchRiskCategories,
  queryKeyRiskCategories,
  type RiskCategoriesQueryData,
};

export type RiskCategoriesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskCategories risk
 *
 * @remarks
 * Return the canonical risk category definitions: metadata (label/description/icon) plus the classification (source / rule_id list / rule_id prefix) used to bucket findings. Dashboards and CLIs should call this instead of maintaining their own copy of the mapping.
 */
export function useRiskCategories(
  request?: ListRiskCategoriesRequest | undefined,
  security?: ListRiskCategoriesSecurity | undefined,
  options?: QueryHookOptions<RiskCategoriesQueryData, RiskCategoriesQueryError>,
): UseQueryResult<RiskCategoriesQueryData, RiskCategoriesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskCategoriesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskCategories risk
 *
 * @remarks
 * Return the canonical risk category definitions: metadata (label/description/icon) plus the classification (source / rule_id list / rule_id prefix) used to bucket findings. Dashboards and CLIs should call this instead of maintaining their own copy of the mapping.
 */
export function useRiskCategoriesSuspense(
  request?: ListRiskCategoriesRequest | undefined,
  security?: ListRiskCategoriesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskCategoriesQueryData,
    RiskCategoriesQueryError
  >,
): UseSuspenseQueryResult<RiskCategoriesQueryData, RiskCategoriesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskCategoriesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskCategoriesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskCategoriesQueryData,
): RiskCategoriesQueryData | undefined {
  const key = queryKeyRiskCategories(...queryKeyBase);

  return client.setQueryData<RiskCategoriesQueryData>(key, data);
}

export function invalidateRiskCategories(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "categories", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskCategories(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "categories", "list"],
  });
}
